// Nieuw AI Voortgang-project: Toolframe-project zoeken, sjabloon, klantportaal-account en
// inbegrepen correctierondes. Het account komt uit de echte portaallijst; bij koppelen
// tonen we Toolframe-klant en portaalaccount naast elkaar ter controle (bevestig).
import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, ButtonBase, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, TextField, Typography,
} from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson, meldFout } from '../../utils/apiFout';
import { bevestig } from '../../utils/bevestig';
import { ACCENT, INKT, LIJN, STIL, haalPortaalAccounts, haalSjablonen } from './voortgangApi';

function projectLabel(p) {
  const nr = p.fields?.Projectnummer;
  return `${nr ? `${nr}. ` : ''}${p.fields?.Projectnaam || ''}`;
}

function accountDetail(a) {
  return [a.domeinen?.length ? a.domeinen.slice(0, 2).join(', ') : null,
    a.gebruikers != null ? `${a.gebruikers} gebruiker${a.gebruikers === 1 ? '' : 's'}` : null,
    a.accountmanager ? `AM ${a.accountmanager.split('@')[0]}` : null].filter(Boolean).join(' · ');
}

// Doorzoekbare accountkeuze: eerst de accounts die bij de klant van het project lijken te
// horen (met de reden), daarna de rest. Zoeken op naam én domein.
export function AccountKeuze({ accounts, fout, waarde, onChange, disabled, klant }) {
  if (fout) {
    return <Typography sx={{ fontSize: 12.5, color: STIL }}>{fout} Je kunt het account later koppelen.</Typography>;
  }
  if (accounts === null) return <CircularProgress size={16} />;
  const gekozen = accounts.find((a) => a.id === waarde) || null;
  const voorgesteld = accounts.filter((a) => a.voorstel).length;
  return (
    <Autocomplete size="small" options={accounts} value={gekozen} disabled={disabled}
      onChange={(_, a) => onChange(a ? a.id : '')}
      groupBy={(a) => (a.voorstel ? `Voorgesteld${klant ? ` voor ${klant}` : ''}` : 'Alle accounts')}
      getOptionLabel={(a) => a.naam || a.id}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={(opties, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        return q ? opties.filter((a) => (a.naam || '').toLowerCase().includes(q)
          || (a.domeinen || []).some((d) => d.toLowerCase().includes(q))) : opties;
      }}
      renderOption={(props, a) => (
        <li {...props} key={a.id}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: a.voorstel ? 700 : 400, color: INKT }}>{a.naam}</Typography>
            <Typography noWrap sx={{ fontSize: 12, color: a.voorstel ? '#15803d' : STIL }}>
              {[a.voorstel?.reden, accountDetail(a)].filter(Boolean).join(' · ') || 'geen domein bekend'}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label="Klantportaal-account" placeholder="Zoek op naam of domein, bv. achmea.nl"
          helperText={gekozen ? accountDetail(gekozen) || 'Wie in het klantportaal akkoord mag geven'
            : voorgesteld ? `${voorgesteld} voorstel${voorgesteld === 1 ? '' : 'len'} voor deze klant bovenaan`
              : 'Leeg laten = nog niet koppelen'} />
      )} />
  );
}

export async function bevestigKoppeling(klantToolframe, account) {
  if (!account) return true;
  return bevestig({
    titel: 'Klantaccount koppelen?',
    tekst: `Toolframe-klant: ${klantToolframe || 'onbekend'} · Klantportaal-account: ${account.naam}. `
      + 'Alle goedgekeurde gebruikers van dit account zien de gedeelde versies en kunnen akkoord geven. '
      + 'Na de eerste keer delen ligt het account vast.',
    knop: 'Koppelen',
  });
}

export default function NieuwProjectDialoog({ open, onClose, onAangemaakt, opslaan }) {
  const [sjablonen, setSjablonen] = useState([]);
  const [accounts, setAccounts] = useState(null);
  const [accountFout, setAccountFout] = useState('');
  const [zoek, setZoek] = useState('');
  const [treffers, setTreffers] = useState(null);
  const [zoeken, setZoeken] = useState(false);
  const [gekozen, setGekozen] = useState(null);
  const [form, setForm] = useState({ sjabloon: 'ai-video-basis', titel: '', portal_account_id: '', revisierondes: 2 });
  const [bezig, setBezig] = useState(false);
  const bezigRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setZoek(''); setTreffers(null); setGekozen(null);
    setForm({ sjabloon: 'ai-video-basis', titel: '', portal_account_id: '', revisierondes: 2 });
    haalSjablonen().then((d) => setSjablonen(d.sjablonen || [])).catch((e) => meldFout(e, 'Sjablonen laden'));
  }, [open]);

  // Accounts (her)laden zodra het project bekend is: dan komen de voorstellen voor die klant
  // bovenaan, en een sterk voorstel (eerder gekoppeld of zelfde domein) staat alvast gekozen.
  useEffect(() => {
    if (!open) return undefined;
    let geldig = true;
    setAccounts(null); setAccountFout('');
    haalPortaalAccounts(gekozen?.id).then((d) => {
      if (!geldig) return;
      const lijst = d.accounts || [];
      setAccounts(lijst);
      const sterk = lijst.filter((a) => (a.voorstel?.score || 0) >= 2);
      if (gekozen && sterk.length === 1) setForm((f) => (f.portal_account_id ? f : { ...f, portal_account_id: sterk[0].id }));
    }).catch((e) => { if (geldig) { setAccounts([]); setAccountFout(e?.message || 'Klantportaal niet bereikbaar.'); } });
    return () => { geldig = false; };
  }, [open, gekozen]);

  useEffect(() => {
    const q = zoek.trim();
    if (gekozen || q.length < 2) { setTreffers(null); return undefined; }
    let geldig = true;
    setZoeken(true);
    const timer = setTimeout(async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/projects?search=${encodeURIComponent(q)}`);
        const d = await leesJson(r, 'Projecten zoeken');
        if (geldig) setTreffers((d.projects || []).slice(0, 10));
      } catch (e) { if (geldig) { meldFout(e, 'Projecten zoeken'); setTreffers([]); } }
      finally { if (geldig) setZoeken(false); }
    }, 350);
    return () => { geldig = false; clearTimeout(timer); };
  }, [zoek, gekozen]);

  const sjabloon = sjablonen.find((s) => s.id === form.sjabloon);

  async function maak() {
    if (!gekozen || bezigRef.current) return;
    const account = (accounts || []).find((a) => a.id === form.portal_account_id) || null;
    const klant = gekozen.fields?.KlantnaamString || '';
    if (!(await bevestigKoppeling(Array.isArray(klant) ? klant.join(', ') : klant, account))) return;
    bezigRef.current = true;
    setBezig(true);
    try {
      const p = await opslaan({
        sjabloon: form.sjabloon,
        project_airtable_id: gekozen.id,
        titel: form.titel.trim(),
        portal_account_id: form.portal_account_id || null,
        revisierondes: Number(form.revisierondes) || 0,
      });
      onAangemaakt(p);
    } catch (e) { meldFout(e, 'Project aanmaken'); }
    finally { bezigRef.current = false; setBezig(false); }
  }

  return (
    <Dialog open={open} onClose={() => { if (!bezig) onClose(); }} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>Nieuw AI Voortgang-project</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {gekozen ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, border: `1px solid ${LIJN}`, borderRadius: 2 }}>
            <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600, color: INKT }}>{projectLabel(gekozen)}</Typography>
            <Button size="small" onClick={() => { setGekozen(null); setForm((f) => ({ ...f, portal_account_id: '' })); }} sx={{ textTransform: 'none' }}>Ander project</Button>
          </Box>
        ) : (
          <Box>
            <TextField fullWidth size="small" autoFocus label="Toolframe-project" placeholder="Zoek op projectnummer of naam…"
              value={zoek} onChange={(e) => setZoek(e.target.value)} />
            <Box sx={{ mt: 0.5, maxHeight: 220, overflowY: 'auto' }}>
              {zoeken && <Box sx={{ py: 1.5, textAlign: 'center' }}><CircularProgress size={16} /></Box>}
              {!zoeken && treffers?.length === 0 && <Typography sx={{ fontSize: 13, color: STIL, py: 1 }}>Geen project gevonden.</Typography>}
              {!zoeken && (treffers || []).map((p) => (
                <ButtonBase key={p.id} onClick={() => { setGekozen(p); setZoek(''); }}
                  sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1, py: 0.75, borderRadius: 1, '&:hover': { bgcolor: '#f3f4f6' } }}>
                  <Typography noWrap sx={{ fontSize: 13.5, color: INKT }}>{projectLabel(p)}</Typography>
                </ButtonBase>
              ))}
            </Box>
          </Box>
        )}

        <TextField select size="small" label="Sjabloon" value={form.sjabloon}
          onChange={(e) => setForm((f) => ({ ...f, sjabloon: e.target.value }))}
          helperText={sjabloon ? sjabloon.fases.map((f) => f.naam).join(' → ') : ' '}>
          {sjablonen.map((s) => <MenuItem key={s.id} value={s.id}>{s.naam}</MenuItem>)}
        </TextField>

        <TextField size="small" label="Titel (optioneel)" placeholder="Standaard de projectnaam" value={form.titel}
          onChange={(e) => setForm((f) => ({ ...f, titel: e.target.value }))} inputProps={{ maxLength: 200 }} />

        <AccountKeuze accounts={accounts} fout={accountFout} waarde={form.portal_account_id}
          klant={Array.isArray(gekozen?.fields?.KlantnaamString) ? gekozen.fields.KlantnaamString.join(', ') : gekozen?.fields?.KlantnaamString}
          onChange={(v) => setForm((f) => ({ ...f, portal_account_id: v }))} />

        <TextField size="small" type="number" label="Inbegrepen correctierondes per item" value={form.revisierondes}
          onChange={(e) => setForm((f) => ({ ...f, revisierondes: e.target.value }))}
          inputProps={{ min: 0, max: 20 }} sx={{ maxWidth: 260 }}
          helperText="Daarboven ziet de klant dat een extra ronde meerkosten kan geven" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={bezig} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" disableElevation onClick={maak} disabled={!gekozen || bezig}
          sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#434f62' } }}>
          {bezig ? 'Bezig…' : 'Aanmaken'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
